import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { Shield, User, Eye } from "lucide-react";
import PasswordStrength from "@/components/PasswordStrength";
import PasswordInput from "@/components/PasswordInput";

const POSITIONS = ["QB", "RB", "WR", "TE", "OL", "DL", "LB", "DB", "DE", "S", "CB", "K"];
const CLASS_YEARS = [2026, 2027, 2028, 2029, 2030];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Lead-source attribution carried by scouting-report QR/CTA links.
  const leadRef = params.get("ref");
  const leadReportToken = params.get("r");
  const [role, setRole] = useState("parent");

  // shared
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");

  // parent-only
  const [athleteFirstName, setAthleteFirstName] = useState("");
  const [athleteLastName, setAthleteLastName] = useState("");
  const [athleteClassYear, setAthleteClassYear] = useState(2028);
  const [athleteHighSchool, setAthleteHighSchool] = useState("");
  const [athletePrimaryPosition, setAthletePrimaryPosition] = useState("WR");
  const [athleteSecondaryPosition, setAthleteSecondaryPosition] = useState("");
  const [athleteBirthdate, setAthleteBirthdate] = useState("");
  const [athleteHometownCity, setAthleteHometownCity] = useState("");
  const [athleteHometownState, setAthleteHometownState] = useState("");

  // recruiter-only
  const [collegeName, setCollegeName] = useState("");
  const [title, setTitle] = useState("");
  const [conference, setConference] = useState("");
  const [schoolWebsiteUrl, setSchoolWebsiteUrl] = useState("");

  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { name, email, password, role, phone };
      if (leadRef) payload.lead_ref = leadRef;
      if (leadReportToken) payload.lead_report_token = leadReportToken;
      if (role === "parent") {
        Object.assign(payload, {
          athlete_first_name: athleteFirstName,
          athlete_last_name: athleteLastName,
          athlete_class_year: Number(athleteClassYear),
          athlete_high_school: athleteHighSchool,
          athlete_primary_position: athletePrimaryPosition,
          athlete_secondary_position: athleteSecondaryPosition || null,
          athlete_birthdate: athleteBirthdate || null,
          athlete_hometown_city: athleteHometownCity,
          athlete_hometown_state: athleteHometownState,
        });
      } else {
        Object.assign(payload, {
          college_name: collegeName,
          title,
          conference,
          school_website_url: schoolWebsiteUrl || null,
        });
      }
      await register(payload);
      toast.success("Account created.");
      nav(role === "parent" ? "/onboarding/parent-intake" : "/dashboard/recruiter");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid lg:grid-cols-2">
      <div className="hidden lg:flex relative flex-col justify-between p-12 overflow-hidden bg-[#0a0a0a] border-r border-white/5">
        <div className="absolute inset-0" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1763639700615-225fe7fdffff)", backgroundSize: "cover", backgroundPosition: "center", opacity: 0.25 }} />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/90" />
        <div className="relative">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-md bg-electric flex items-center justify-center">
              <Shield size={20} className="text-black" />
            </div>
            <div className="font-display text-2xl font-black tracking-[0.18em]">VERIFIED</div>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-display text-5xl xl:text-6xl font-black uppercase tracking-[0.01em] leading-[0.82]">
            Build the<br />
            <span className="text-electric">verified</span><br />
            generation.
          </h2>

          {/* Founder trust nudge — quick credibility anchor at the conversion moment */}
          <div className="mt-10 flex items-start gap-4 p-4 border border-white/10 bg-black/40 backdrop-blur-sm rounded-lg max-w-sm" data-testid="register-founder-trust">
            <Link to="/about" className="shrink-0">
              <img
                src="/founder/brandon-taylor.jpeg"
                alt="Brandon Taylor, Founder"
                className="w-14 h-14 rounded-full object-cover border-2 border-electric/60"
                loading="lazy"
              />
            </Link>
            <div className="text-xs leading-relaxed">
              <div className="text-white/90 font-medium italic">
                "Built so families finally have access to the same caliber of evaluation NFL teams expect."
              </div>
              <div className="mt-2 text-[10px] tracking-[0.18em] uppercase font-bold text-electric">
                Brandon Taylor · Founder
              </div>
              <div className="text-[10px] text-white/50 mt-0.5">
                20+ Yrs NFL Scouting · Panthers · Titans · NFL Combine
              </div>
            </div>
          </div>
        </div>
        <div className="relative text-xs text-white/30">© OmniSportsGroup</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 overflow-y-auto">
        <div className="w-full max-w-md py-6">
          <div className="label-mono mb-3">Create account</div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
            Get VERIFIED.
          </h1>
          <p className="text-sm text-white/50 mt-2">
            Takes 90 seconds. We'll only ask for what we need to set up your account.
          </p>

          <div className="mt-6 grid grid-cols-2 gap-2">
            <RoleTile
              active={role === "parent"}
              onClick={() => setRole("parent")}
              icon={<User size={16} />}
              title="Parent / Athlete"
              sub="Create your athlete profile"
              testid="role-parent"
            />
            <RoleTile
              active={role === "recruiter"}
              onClick={() => setRole("recruiter")}
              icon={<Eye size={16} />}
              title="College Recruiter"
              sub="Scout verified athletes"
              testid="role-recruiter"
            />
          </div>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="register-form">
            {/* SHARED — Your account */}
            <div className="label-mono text-electric pt-2">
              {role === "parent" ? "Your Info (the parent / guardian)" : "Your Info (the coach)"}
            </div>

            <Fld
              label={role === "parent" ? "Your full name" : "Your full name"}
              hint={role === "parent" ? "The parent or guardian managing this account — not the athlete's name." : "Use the name your school directory lists you under."}
            >
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="input-dark w-full rounded-md px-4 py-3 text-sm"
                placeholder={role === "parent" ? "e.g. Marcus Hill Sr." : "e.g. Coach Sarah Anders"}
                data-testid="register-name-input"
              />
            </Fld>

            <Fld
              label="Email address"
              hint={role === "parent" ? "We'll use this for receipts, session reminders, and recruiter alerts." : "Use your school email (e.g. .edu) — this speeds up the 24-hour identity review."}
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-dark w-full rounded-md px-4 py-3 text-sm"
                placeholder={role === "parent" ? "e.g. you@gmail.com" : "e.g. sanders@uga.edu"}
                data-testid="register-email-input"
              />
            </Fld>

            <Fld
              label="Mobile phone"
              hint={role === "parent" ? "Optional — only used for session-day text reminders." : "Optional — only used if our team needs to confirm your identity."}
            >
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="input-dark w-full rounded-md px-4 py-3 text-sm"
                placeholder="(555) 123-4567"
                data-testid="register-phone-input"
              />
            </Fld>

            <Fld
              label="Create a password"
              hint="At least 8 characters with one letter and one number."
            >
              <PasswordInput
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                className="input-dark w-full rounded-md px-4 py-3 text-sm"
                placeholder="At least 8 characters · letter + number"
                testId="register-password-input"
              />
              <PasswordStrength value={password} />
            </Fld>

            {/* PARENT */}
            {role === "parent" && (
              <div className="rounded-md border border-white/10 bg-[#0a0a0a] p-4 space-y-3" data-testid="register-parent-section">
                <div>
                  <div className="label-mono text-electric">Your Athlete</div>
                  <p className="text-[11px] text-white/50 mt-1 leading-relaxed">
                    This creates the public profile recruiters will see. You can edit any of this later — no measurables required yet.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Fld label="Athlete first name" hint="Legal first name.">
                    <input
                      required
                      value={athleteFirstName}
                      onChange={(e) => setAthleteFirstName(e.target.value)}
                      className="input-dark w-full rounded-md px-3 py-2 text-sm"
                      placeholder="e.g. Marcus"
                      data-testid="register-athlete-first"
                    />
                  </Fld>
                  <Fld label="Athlete last name" hint="Legal last name (matches HS roster).">
                    <input
                      required
                      value={athleteLastName}
                      onChange={(e) => setAthleteLastName(e.target.value)}
                      className="input-dark w-full rounded-md px-3 py-2 text-sm"
                      placeholder="e.g. Hill"
                      data-testid="register-athlete-last"
                    />
                  </Fld>

                  <Fld label="Graduation year" hint="The year they finish high school.">
                    <select
                      value={athleteClassYear}
                      onChange={(e) => setAthleteClassYear(e.target.value)}
                      className="input-dark w-full rounded-md px-3 py-2 text-sm"
                      data-testid="register-athlete-year"
                    >
                      {CLASS_YEARS.map((y) => <option key={y} value={y}>{`Class of ${y}`}</option>)}
                    </select>
                  </Fld>
                  <Fld label="Primary position" hint="The one they line up at most.">
                    <select
                      value={athletePrimaryPosition}
                      onChange={(e) => setAthletePrimaryPosition(e.target.value)}
                      className="input-dark w-full rounded-md px-3 py-2 text-sm"
                      data-testid="register-athlete-position"
                    >
                      {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Fld>

                  <div className="col-span-2">
                    <Fld
                      label="Secondary position"
                      hint="Optional — if they play both sides of the ball or a backup spot (e.g. WR/CB)."
                    >
                      <select
                        value={athleteSecondaryPosition}
                        onChange={(e) => setAthleteSecondaryPosition(e.target.value)}
                        className="input-dark w-full rounded-md px-3 py-2 text-sm"
                        data-testid="register-athlete-secondary-position"
                      >
                        <option value="">— None —</option>
                        {POSITIONS.filter((p) => p !== athletePrimaryPosition).map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </Fld>
                  </div>

                  <div className="col-span-2">
                    <Fld label="High school" hint="Full school name (recruiters search by this).">
                      <input
                        required
                        value={athleteHighSchool}
                        onChange={(e) => setAthleteHighSchool(e.target.value)}
                        className="input-dark w-full rounded-md px-3 py-2 text-sm"
                        placeholder="e.g. Westlake High School"
                        data-testid="register-athlete-school"
                      />
                    </Fld>
                  </div>

                  <Fld label="Hometown city" hint="Where the school is located.">
                    <input
                      value={athleteHometownCity}
                      onChange={(e) => setAthleteHometownCity(e.target.value)}
                      className="input-dark w-full rounded-md px-3 py-2 text-sm"
                      placeholder="e.g. Atlanta"
                      data-testid="register-athlete-hometown-city"
                    />
                  </Fld>
                  <Fld label="State" hint="2-letter abbreviation (GA, FL, TX, …).">
                    <input
                      value={athleteHometownState}
                      onChange={(e) => setAthleteHometownState(e.target.value.toUpperCase())}
                      className="input-dark w-full rounded-md px-3 py-2 text-sm uppercase"
                      placeholder="GA"
                      maxLength={2}
                      data-testid="register-athlete-hometown-state"
                    />
                  </Fld>

                  <div className="col-span-2">
                    <Fld
                      label="Athlete date of birth"
                      hint="Optional — used to compute age at testing. We never display it publicly."
                    >
                      <input
                        type="date"
                        value={athleteBirthdate}
                        onChange={(e) => setAthleteBirthdate(e.target.value)}
                        className="input-dark w-full rounded-md px-3 py-2 text-sm"
                        data-testid="register-athlete-birthdate"
                      />
                    </Fld>
                  </div>
                </div>

                <div className="text-[10px] text-white/40 leading-relaxed pt-2 border-t border-white/5">
                  <strong className="text-white/60">Next step:</strong> after registering, you'll sign the Parent Intake packet and then book a VERIFIED testing session to add measurables to the profile.
                </div>
              </div>
            )}

            {/* RECRUITER */}
            {role === "recruiter" && (
              <div className="rounded-md border border-white/10 bg-[#0a0a0a] p-4 space-y-3" data-testid="register-recruiter-section">
                <div>
                  <div className="label-mono text-electric">School Affiliation</div>
                  <p className="text-[11px] text-white/50 mt-1 leading-relaxed">
                    We hand-verify every recruiter account against your school's published directory before granting access. This protects athletes and their families from non-college actors. Typical review: <strong className="text-white/70">under 24 hours</strong>.
                  </p>
                </div>

                <Fld
                  label="School / program name"
                  hint="Full name as it appears in your athletic department. Don't abbreviate."
                >
                  <input
                    required
                    value={collegeName}
                    onChange={(e) => setCollegeName(e.target.value)}
                    className="input-dark w-full rounded-md px-3 py-2 text-sm"
                    placeholder="e.g. University of Georgia"
                    data-testid="register-college-name"
                  />
                </Fld>

                <div className="grid grid-cols-2 gap-3">
                  <Fld
                    label="Your job title"
                    hint="What's on your staff card (e.g. Recruiting Coordinator, OL Coach, GA)."
                  >
                    <input
                      required
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="input-dark w-full rounded-md px-3 py-2 text-sm"
                      placeholder="e.g. Recruiting Coordinator"
                      data-testid="register-recruiter-title"
                    />
                  </Fld>
                  <Fld
                    label="Conference"
                    hint="e.g. SEC, Big 10, ACC, Big 12, Pac-12, MAC, FCS, D2, D3, NAIA, JUCO."
                  >
                    <input
                      value={conference}
                      onChange={(e) => setConference(e.target.value)}
                      className="input-dark w-full rounded-md px-3 py-2 text-sm"
                      placeholder="e.g. SEC"
                      data-testid="register-recruiter-conference"
                    />
                  </Fld>
                </div>

                <Fld
                  label="School coaching-staff page URL (optional)"
                  hint="Public URL of your athletics staff/recruiting page. Helps athletes verify who you are."
                >
                  <input
                    type="url"
                    value={schoolWebsiteUrl}
                    onChange={(e) => setSchoolWebsiteUrl(e.target.value)}
                    className="input-dark w-full rounded-md px-3 py-2 text-sm"
                    placeholder="https://georgiadogs.com/sports/football/coaches"
                    data-testid="register-recruiter-school-url"
                  />
                </Fld>

                <div className="text-[10px] text-white/40 leading-relaxed pt-2 border-t border-white/5">
                  <strong className="text-white/60">Two-step verification:</strong> (1) confirm your email · (2) our team manually verifies your role against your school's athletic directory. You'll receive an email when your account is activated.
                </div>
              </div>
            )}

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="register-submit-btn">
              {busy ? "Creating…" : "Create Account"}
            </button>
          </form>

          <div className="mt-6 text-sm text-white/50 text-center">
            Have an account?{" "}
            <Link to="/login" className="text-electric hover:underline">
              Log in
            </Link>
          </div>
          <p className="text-[10px] text-white/30 mt-6 leading-relaxed">
            By creating an account you acknowledge VERIFIED evaluations represent independent
            scouting opinions and do not guarantee recruitment outcomes or scholarship offers.
          </p>
        </div>
      </div>
    </div>
  );
}

function Fld({ label, hint, children }) {
  return (
    <div>
      <label className="label-mono block mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-white/40 mt-1.5 leading-relaxed">{hint}</p>}
    </div>
  );
}

function RoleTile({ active, onClick, icon, title, sub, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-4 rounded-md border transition-all ${
        active
          ? "border-electric bg-electric/5 shadow-[inset_0_0_18px_rgba(0,229,255,0.08)]"
          : "border-white/10 bg-[#0a0a0a] hover:border-white/20"
      }`}
    >
      <div className={`flex items-center gap-2 ${active ? "text-electric" : "text-white/60"}`}>
        {icon}
        <div className="font-display text-sm font-bold uppercase tracking-wider">{title}</div>
      </div>
      <div className="text-xs text-white/40 mt-1">{sub}</div>
    </button>
  );
}
